import {
  objectParser,
  booleanAt,
  identifierAt,
  nonBlankTextAt,
  textAt,
  oneOf,
  text,
  MAX_MESSAGE_TEXT,
  integerAt,
  arrayOf,
  numberAt,
  assertUnique,
  assertJsonByteSize,
  MAX_DOCUMENT_PAYLOAD_BYTES,
  fail
} from './primitives';
import {
  type LearningSettings,
  type LearningEntry,
  MAX_LEARNING_OBSERVATIONS,
  MAX_LEARNING_VALUE_BYTES,
  LEARNING_ENTRY_STATUSES,
  type LearningOperation,
  type LearningSnapshot,
  type LearningDelivery,
  LEARNING_DELIVERY_STATUSES
} from '../dtos';
import { type LearningSettingsUpdatePayload, type LearningEntriesPayload } from '../protocol';

export const parseLearningSettings = objectParser<LearningSettings>({
  learnFromFormEdits: booleanAt,
  saveMissingInformation: booleanAt
});

export const parseLearningSettingsUpdate = objectParser<LearningSettingsUpdatePayload>(
  {},
  { optional: { learnFromFormEdits: booleanAt, saveMissingInformation: booleanAt } }
);

const learningEntryAt = objectParser<LearningEntry>(
  {
    id: identifierAt,
    fieldLabel: nonBlankTextAt,
    value: (value, path) => {
      const parsed = textAt(value, path, MAX_LEARNING_VALUE_BYTES);
      if (new TextEncoder().encode(parsed).byteLength > MAX_LEARNING_VALUE_BYTES)
        fail(path, `must be at most ${MAX_LEARNING_VALUE_BYTES} bytes`);
      return parsed;
    },
    status: oneOf(LEARNING_ENTRY_STATUSES),
    hintLearned: booleanAt,
    canApply: booleanAt,
    canUndo: booleanAt
  },
  { optional: { entityLabel: nonBlankTextAt, message: text(MAX_MESSAGE_TEXT) } }
);

const learningOperationAt = objectParser<LearningOperation>(
  {
    id: identifierAt,
    bucketId: identifierAt,
    bucketRevision: integerAt,
    entries: arrayOf(learningEntryAt, MAX_LEARNING_OBSERVATIONS),
    modelCalls: integerAt,
    elapsedMs: (value, path) => numberAt(value, path, 0, Number.MAX_SAFE_INTEGER)
  },
  {
    optional: { checkpointId: identifierAt },
    refine: (object, path) =>
      assertUnique(
        object.entries as LearningEntry[],
        (entry) => entry.id,
        `${path}.entries`,
        'entry ID'
      )
  }
);

const learningDeliveryAt = objectParser<LearningDelivery>(
  {
    operationId: identifierAt,
    checkpointId: identifierAt,
    bucketId: identifierAt,
    observationCount: (value, path) => integerAt(value, path, 200),
    status: oneOf(LEARNING_DELIVERY_STATUSES)
  },
  { optional: { message: text(MAX_MESSAGE_TEXT) } }
);

export const parseLearningSnapshot = objectParser<LearningSnapshot>(
  {
    active: booleanAt,
    operations: arrayOf(learningOperationAt, 100),
    settings: parseLearningSettings
  },
  {
    optional: {
      stagedCount: integerAt,
      deliveries: arrayOf(learningDeliveryAt, 100)
    },
    refine: (object, path) => {
      assertUnique(
        object.operations as LearningOperation[],
        (operation) => operation.id,
        `${path}.operations`,
        'operation ID'
      );
      if (object.deliveries)
        assertUnique(
          object.deliveries as LearningDelivery[],
          (delivery) => delivery.operationId,
          `${path}.deliveries`,
          'operation ID'
        );
      assertJsonByteSize(object, path, MAX_DOCUMENT_PAYLOAD_BYTES);
    }
  }
);

export const learningEntriesPayloadAt = objectParser<LearningEntriesPayload>(
  { operationId: identifierAt, entryIds: arrayOf(identifierAt, MAX_LEARNING_OBSERVATIONS) },
  {
    refine: (object, path) => {
      const ids = object.entryIds as string[];
      if (!ids.length) fail(`${path}.entryIds`, 'must identify at least one entry');
      assertUnique(ids, (id) => id, `${path}.entryIds`, 'entry ID');
    }
  }
);
